import { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Menu, ScrollArea } from '@mantine/core';
import { TFunction } from 'i18next';

import { Button, SectionLabel, Text } from 'components';
import { ICreditAttachment } from 'types';
import { useTranslation } from 'react-i18next';
import i18n from 'config/localization';
import { StorageUtils } from 'utils';
import { AppConstants } from '../../../constants';
import moment from 'moment';

const { STORAGE_USER_DOCUMENTATION_LANGUAGE, DE_FLAG, EN_FLAG, SR_FLAG } = AppConstants;

const languages = [
  { key: 'de', label: 'german', flag: DE_FLAG },
  { key: 'sr', label: 'serbian', flag: SR_FLAG },
  { key: 'en', label: 'english', flag: EN_FLAG },
];

interface IRequiredDocumentation {
  requiredAttachments: ICreditAttachment[] | undefined;
  t: TFunction;
}

interface ICreditRequiredDocumentationItemProps {
  item: ICreditAttachment;
  language: string;
}

const RequiredDocumentation = ({
  requiredAttachments,
  t,
}: IRequiredDocumentation) => {
  const initialLang = i18n.language;
  const [language, setLanguage] = useState<string>(initialLang ? initialLang.split('-')[0] : 'de');
  const [undeliveredAttachments, setUndeliveredAttachments] = useState<ICreditAttachment[] | undefined>(undefined);

  useEffect(() => {
    setUndeliveredAttachments(requiredAttachments ? requiredAttachments.filter((el: ICreditAttachment) => !el.delivered) : undefined )
  }, [requiredAttachments]);

  useEffect(() => {
    const storageLanguage = StorageUtils.getString(STORAGE_USER_DOCUMENTATION_LANGUAGE);
    if (storageLanguage) {
      setLanguage(storageLanguage);
    }
  }, []);

  const changeLanguage = useCallback((lang: string) => {
    StorageUtils.setString(STORAGE_USER_DOCUMENTATION_LANGUAGE, lang);
    setLanguage(lang);
  }, []);

  return (
    <Box h={300} p={'md'} bg={'#F1F2F3'}>
      <Flex align={'center'} mr={'md'}>
        <SectionLabel label={t('credit.documentation.required')} variant={'green'} pb={'sm'} pr={'sm'} />
        <Menu>
          <Menu.Target>
            <Button size={'compact-sm'} variant={'light'} bg={'#439425'} radius={30} mb={'xs'} pl={2} pr={2}>
              <Text style={{ color: 'white', width: 30, height: 30, fontSize: 16, paddingTop: 2 }}>{languages.find(el => el.key === language)?.key.toUpperCase()}</Text>
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {languages.map((el, index) => (
              <Menu.Item key={`language-${index}`} onClick={() => changeLanguage(el.key)}>
                {t(`language.${el.label}`)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </Flex>
      <ScrollArea
        h={240}
        type={'scroll'}
        overscrollBehavior={'contain'}
      >
        {undeliveredAttachments ?
          undeliveredAttachments.length === 0 ? <Text>{t('credit.documentation.undelivered.no')}</Text>
          : undeliveredAttachments.map((item) => <CreditRequiredDocumentationItem item={item} language={language} />)
        : undefined}
      </ScrollArea>
    </Box>
  )
}

const CreditRequiredDocumentationItem = ({
  item,
  language,
}: ICreditRequiredDocumentationItemProps) => {
  const { t } = useTranslation('translation', { lng: language.toLowerCase() });

  const getName = useCallback(() => {
    let localizedMessage = item.name;
    if (item.localizationKey) {
      localizedMessage = t(`credit.documentation.type.${item.localizationKey}`);
      if (item.localizationParams) {
        const localizationParams = item.localizationParams.split(',');
        localizedMessage = localizedMessage
          .replace(/{{date,\s*(.*?),\s*'(.*?)'}}/g, (_, format, separator) => {
            return localizationParams
              .map(date => moment(date).locale(language).format(format))
              .join(separator);
          })
          // Handle single dates by index, e.g. {{date0, yyyy}}, {{date1, MMMM yyyy}}
          .replace(/{{date(\d+),\s*(.*?)}}/g, (_, index, format) => {
            const date = localizationParams[parseInt(index, 10)];
            return date ? moment(date).locale(language).format(format) : '';
          });
      }

      if (item.additionalLocalizationKey) {
        let additional = '';
        item.additionalLocalizationKey.split(',').forEach(el => {
          if (additional.length > 0) {
            additional += ', ';
          }
          additional += t(`credit.documentation.type.additional.${item.type.toLowerCase()}.${el}`)
        });
        localizedMessage += ' (' + additional + ')';
      }
      if (item.partner) {
        localizedMessage += ' - ' + t(`credit.documentation.type.additional.partner`);
      }
      return localizedMessage;
    }
    return item.name;
  }, [item, language, t]);

  return (
    <Text
      key={`attachment-${item.id}`}
      c={item.delivered ? 'gray' : '#378F0E'}
      pb={'xs'}
      fw={item.delivered ? 'bold' : undefined}
    >
      {getName()}
    </Text>
  )
}

export default RequiredDocumentation;